import { Milestone } from '@/models/MilestoneModel';
import { client } from './API';
import { Card, CardAttachment } from '@/models/card/CardModel';
import { Comment } from '@/models/CommentModel';
import { ENVIRONMENTAL_VARIABLES } from '@/utils/Environment';

/**
 * Fetch list of milestones
 */
export const fetchMilestones = ({
  boardId,
  communityId,
  roomId,
}: {
  boardId: string;
  communityId: string;
  roomId: string;
}): Promise<Milestone[]> => {
  return client
    .get(`${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/milestones`)
    .then((response) => response.data);
};

/**
 * Create milestone
 */
export const createMilestone = ({
  boardId,
  containerId,
  data,
  communityId,
  roomId,
}: {
  boardId: string;
  containerId: string;
  data: Milestone;
  communityId: string;
  roomId: string;
}): Promise<Milestone> => {
  const config = { params: { milestone_set_id: containerId } };

  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/milestones`,
      data,
      config,
    )
    .then((response) => response.data);
};

/**
 * Update milestone values
 */
export const updateMilestone = ({
  boardId,
  milestoneId,
  data,
  communityId,
  roomId,
}: {
  boardId: string;
  milestoneId: string;
  data: Milestone;
  communityId: string;
  roomId: string;
}): Promise<Milestone> => {
  return client
    .put(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/milestones/${milestoneId}`,
      data,
    )
    .then((response) => response.data);
};

/**
 * Delete milestone
 */
export const deleteMilestone = ({
  boardId,
  milestoneId,
  communityId,
  roomId,
}: {
  boardId: string;
  milestoneId: string;
  communityId: string;
  roomId: string;
}): Promise<Milestone> => {
  return client
    .delete(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/milestones/${milestoneId}`,
    )
    .then((response) => response.data);
};

/**
 * Fetch milestone attachment infos
 */
export const fetchMilestoneAttachments = ({
  boardId,
  milestoneId,
  communityId,
  roomId,
}: {
  boardId: string;
  milestoneId: string;
  communityId: string;
  roomId: string;
}): Promise<CardAttachment[]> => {
  return client
    .get(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${milestoneId}/attachments`,
    )
    .then((response) => response.data);
};

/**
 * Upload attachment with content to milestone
 */
export const uploadMilestoneAttachment = ({
  boardId,
  milestoneId,
  file,
  communityId,
  roomId,
}: {
  boardId: string;
  milestoneId: string;
  file: File;
  communityId: string;
  roomId: string;
}): Promise<CardAttachment> => {
  const data = new FormData();
  data.append('file', file, file.name);

  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${milestoneId}/files`,
      data,
      { headers: { Accept: '*/*', 'Content-Type': 'multipart/form-data' } },
    )
    .then((response) => response.data);
};

/**
 * Add link to milestone
 */
export const addMilestoneLink = ({
  boardId,
  milestoneId,
  link,
  communityId,
  roomId,
}: {
  boardId: string;
  milestoneId: string;
  link: any;
  communityId: string;
  roomId: string;
}): Promise<Card> => {
  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${milestoneId}/links`,
      link,
    )
    .then((response) => response.data);
};

/**
 * Delete attachment from milestone
 */
export const deleteMilestoneAttachment = ({
  boardId,
  milestoneId,
  attachmentId,
  communityId,
  roomId,
}: {
  boardId: string;
  milestoneId: string;
  attachmentId: string;
  communityId: string;
  roomId: string;
}): Promise<Card> => {
  return client
    .delete(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${milestoneId}/attachments/${attachmentId}`,
    )
    .then((response) => response.data);
};

export const downloadMilestoneAttachment = ({
  boardId,
  milestoneId,
  attachmentId,
  version,
  communityId,
  roomId,
}: {
  boardId: string;
  milestoneId: string;
  attachmentId: string;
  version: string;
  communityId: string;
  roomId: string;
}): Promise<Blob> => {
  const config = {
    responseType: 'blob', // important
    params: version ? { version } : {},
  };
  return client.get(
    `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${milestoneId}/files/${attachmentId}`,
    config as any,
  );
};

/**
 * Fetch all milestone comments
 */
export const fetchMilestoneComments = ({
  boardId,
  milestoneId,
  communityId,
  roomId,
}: {
  boardId: string;
  milestoneId: string;
  communityId: string;
  roomId: string;
}): Promise<Comment[]> => {
  return client
    .get(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${milestoneId}/comments`,
    )
    .then((response) => response.data);
};

/**
 * Create comment in milestone
 */
export const createMilestoneComment = ({
  boardId,
  milestoneId,
  content,
  communityId,
  roomId,
}: {
  boardId: string;
  milestoneId: string;
  content: string;
  communityId: string;
  roomId: string;
}): Promise<Comment> => {
  const comment = { content };

  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${milestoneId}/comments`,
      comment,
    )
    .then((response) => response.data);
};

/**
 * Update comment in Milestone
 */
export const updateMilestoneComment = ({
  boardId,
  milestoneId,
  comment,
  communityId,
  roomId,
}: {
  boardId: string;
  milestoneId: string;
  comment: Comment;
  communityId: string;
  roomId: string;
}): Promise<Comment> => {
  return client
    .put(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${milestoneId}/comments/${comment.id}`,
      comment,
    )
    .then((response) => response.data);
};

/**
 * Delete comment in Milestone
 */
export const deleteMilestoneComment = ({
  boardId,
  milestoneId,
  commentId,
  communityId,
  roomId,
}: {
  boardId: string;
  milestoneId: string;
  commentId: string;
  communityId: string;
  roomId: string;
}): Promise<Comment> => {
  return client
    .delete(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${milestoneId}/comments/${commentId}`,
    )
    .then((response) => response.data);
};
